import React, { createContext, useContext, useEffect, useRef, useCallback, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useLocation } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { TimelineEntry } from '@/types';

// Efficient real-time manager with CPU optimization
class EfficientRealtimeManager {
  private static instance: EfficientRealtimeManager | null = null;
  private activeChannels: Map<string, any> = new Map();
  private queryClient: any = null;
  private isTabVisible = true;
  private lastActivity = Date.now();
  private activityTimeout: NodeJS.Timeout | null = null;
  private batchedUpdates: Map<string, any[]> = new Map();
  private batchTimeout: NodeJS.Timeout | null = null;

  static getInstance(): EfficientRealtimeManager {
    if (!EfficientRealtimeManager.instance) {
      EfficientRealtimeManager.instance = new EfficientRealtimeManager();
      this.instance.setupActivityTracking();
    }
    return EfficientRealtimeManager.instance;
  }

  private setupActivityTracking() {
    // Track tab visibility to pause subscriptions when not active
    document.addEventListener('visibilitychange', () => {
      this.isTabVisible = !document.hidden;
      if (this.isTabVisible) {
        this.updateActivity();
      }
    });

    // Track user activity
    ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart'].forEach(event => {
      document.addEventListener(event, () => this.updateActivity(), { passive: true });
    });
  }

  private updateActivity() {
    this.lastActivity = Date.now();
    if (this.activityTimeout) {
      clearTimeout(this.activityTimeout);
    }
    
    // Auto-cleanup after 10 minutes of inactivity
    this.activityTimeout = setTimeout(() => {
      this.cleanupInactiveChannels();
    }, 10 * 60 * 1000);
  }

  private cleanupInactiveChannels() {
    if (!this.isTabVisible || Date.now() - this.lastActivity > 10 * 60 * 1000) {
      console.log('🧹 Efficient: Cleaning up inactive channels');
      this.activeChannels.forEach((channel, key) => {
        supabase.removeChannel(channel);
      });
      this.activeChannels.clear();
    }
  }

  setQueryClient(client: any) {
    this.queryClient = client;
  }

  // Context-aware subscription - only for timeline routes and active tabs
  subscribeToTimeline(userId: string, targetUserId: string, callbacks: {
    onTimelineEntry: (payload: any) => void;
    onStatusChange: (status: string) => void;
  }) {
    // Don't subscribe if tab is not visible or user is inactive
    if (!this.isTabVisible || Date.now() - this.lastActivity > 5 * 60 * 1000) {
      console.log('🚫 Efficient: Skipping subscription - tab inactive or user away');
      return () => {};
    }

    const channelKey = `timeline-${targetUserId}`;
    
    // Reuse existing channel if available
    if (this.activeChannels.has(channelKey)) {
      console.log('♻️ Efficient: Reusing existing channel:', channelKey);
      return () => {};
    }

    console.log('🚀 Efficient: Creating targeted timeline subscription for:', targetUserId);
    this.updateActivity();

    // Create focused subscription for specific timeline with time filter
    const channel = supabase
      .channel(`efficient-timeline-${targetUserId}`, {
        config: {
          broadcast: { self: false },
          presence: { key: userId }
        }
      })
      // Only listen for INSERTs and DELETEs on timeline entries for the target user
      .on('postgres_changes', {
        event: 'INSERT',
        schema: 'public',
        table: 'profile_timeline_entries',
        filter: `user_id=eq.${targetUserId}`
      }, (payload) => this.batchUpdate('timeline_entry', payload, callbacks.onTimelineEntry))
      .on('postgres_changes', {
        event: 'DELETE',
        schema: 'public',
        table: 'profile_timeline_entries',
        filter: `user_id=eq.${targetUserId}`
      }, (payload) => this.batchUpdate('timeline_entry', payload, callbacks.onTimelineEntry))
      .subscribe((status) => {
        console.log(`📡 Efficient timeline status: ${status}`);
        callbacks.onStatusChange(status);
      });

    this.activeChannels.set(channelKey, channel);

    return () => {
      if (this.activeChannels.has(channelKey)) {
        console.log('🧹 Efficient: Cleaning up timeline subscription');
        supabase.removeChannel(this.activeChannels.get(channelKey));
        this.activeChannels.delete(channelKey);
      }
    };
  }

  // Minimal notification subscription only when needed
  subscribeToNotifications(userId: string, callbacks: {
    onNotification: (payload: any) => void;
    onFriendRequest: (payload: any) => void;
    onStatusChange: (status: string) => void;
  }) {
    if (!this.isTabVisible) return () => {};

    const channelKey = `notifications-${userId}`;
    
    if (this.activeChannels.has(channelKey)) {
      return () => {};
    }

    console.log('🔔 Efficient: Creating notification subscription for:', userId);
    
    const channel = supabase
      .channel(`efficient-notifications-${userId}`)
      // Only recent notifications (last 24 hours) to reduce payload
      .on('postgres_changes', {
        event: 'INSERT',
        schema: 'public',
        table: 'notifications',
        filter: `user_id=eq.${userId}`
      }, (payload) => this.batchUpdate('notification', payload, callbacks.onNotification))
      // Only friend request INSERTs for immediate response
      .on('postgres_changes', {
        event: 'INSERT',
        schema: 'public',
        table: 'friends',
        filter: `friend_id=eq.${userId}`
      }, (payload) => this.batchUpdate('friend_request', payload, callbacks.onFriendRequest))
      .subscribe(callbacks.onStatusChange);

    this.activeChannels.set(channelKey, channel);

    return () => {
      if (this.activeChannels.has(channelKey)) {
        supabase.removeChannel(this.activeChannels.get(channelKey));
        this.activeChannels.delete(channelKey);
      }
    };
  }

  // Batch updates to reduce CPU load
  private batchUpdate(type: string, payload: any, callback: (payload: any) => void) {
    if (!this.batchedUpdates.has(type)) {
      this.batchedUpdates.set(type, []);
    }
    
    this.batchedUpdates.get(type)!.push(payload);

    if (this.batchTimeout) {
      clearTimeout(this.batchTimeout);
    }

    // Process batched updates every 500ms
    this.batchTimeout = setTimeout(() => {
      this.batchedUpdates.forEach((updates, updateType) => {
        if (updates.length > 0) {
          // Process most recent update only to avoid overwhelming the UI
          const mostRecent = updates[updates.length - 1];
          callback(mostRecent);
        }
      });
      this.batchedUpdates.clear();
      this.batchTimeout = null;
    }, 500);
  }

  cleanup() {
    this.activeChannels.forEach((channel) => {
      supabase.removeChannel(channel);
    });
    this.activeChannels.clear();
    
    if (this.activityTimeout) {
      clearTimeout(this.activityTimeout);
    }
    if (this.batchTimeout) {
      clearTimeout(this.batchTimeout);
    }
  }

  getActiveSubscriptions() {
    return this.activeChannels.size;
  }
}

// Global instance
const efficientManager = EfficientRealtimeManager.getInstance();

interface EfficientRealtimeContextType {
  subscriptionCount: number;
  isConnected: boolean;
}

const EfficientRealtimeContext = createContext<EfficientRealtimeContextType>({
  subscriptionCount: 0,
  isConnected: false
});

export const useEfficientRealtime = () => useContext(EfficientRealtimeContext);

interface EfficientRealtimeProviderProps {
  children: React.ReactNode;
}

export const EfficientRealtimeProvider: React.FC<EfficientRealtimeProviderProps> = ({ children }) => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const location = useLocation();
  const [isConnected, setIsConnected] = useState(false);
  const cleanupFunctionsRef = useRef<(() => void)[]>([]);

  // Set query client
  useEffect(() => {
    efficientManager.setQueryClient(queryClient);
  }, [queryClient]);

  // Reduced monitoring - every 5 minutes instead of every minute
  useEffect(() => {
    const interval = setInterval(() => {
      console.log(`📊 Efficient: Active subscriptions: ${efficientManager.getActiveSubscriptions()}`);
    }, 5 * 60 * 1000);
    
    return () => clearInterval(interval);
  }, []);

  // Smart cache updates for timeline entries
  const updateTimelineCache = useCallback((payload: any, targetUserId: string) => {
    if (payload.eventType === 'INSERT') {
      const newEntry = payload.new as TimelineEntry;
      queryClient.setQueryData(
        ['timeline-entries', targetUserId],
        (oldData: TimelineEntry[] | undefined) => {
          if (!oldData) return [newEntry];
          const exists = oldData.some(entry => entry.id === newEntry.id);
          if (exists) return oldData;
          return [newEntry, ...oldData].sort((a, b) => {
            const dateA = new Date(a.date || a.created_at);
            const dateB = new Date(b.date || b.created_at);
            return dateB.getTime() - dateA.getTime();
          });
        }
      );
    } else if (payload.eventType === 'DELETE') {
      const deletedEntry = payload.old as TimelineEntry;
      queryClient.setQueryData(
        ['timeline-entries', targetUserId],
        (oldData: TimelineEntry[] | undefined) => {
          if (!oldData) return oldData;
          return oldData.filter(entry => entry.id !== deletedEntry.id);
        }
      );
    }
  }, [queryClient]);

  // Context-aware subscriptions based on current route
  useEffect(() => {
    if (!user?.id) {
      // Cleanup on logout
      cleanupFunctionsRef.current.forEach(cleanup => cleanup());
      cleanupFunctionsRef.current = [];
      setIsConnected(false);
      return;
    }

    // Only subscribe when on timeline routes
    const isTimelineRoute = location.pathname.startsWith('/timeline');
    
    if (!isTimelineRoute) {
      console.log('📍 Efficient: Not on timeline route, minimal subscriptions only');
      
      // Only notification subscription for non-timeline routes
      const notificationCleanup = efficientManager.subscribeToNotifications(user.id, {
        onNotification: (payload) => {
          console.log('🔔 Efficient: Notification update:', payload.eventType);
          // Lightweight notification invalidation
          queryClient.invalidateQueries({ queryKey: ['notifications', user.id] });
        },
        onFriendRequest: (payload) => {
          console.log('👥 Efficient: Friend request:', payload.eventType);
          queryClient.invalidateQueries({ queryKey: ['friends', user.id] });
        },
        onStatusChange: (status) => {
          setIsConnected(status === 'SUBSCRIBED');
        }
      });
      
      cleanupFunctionsRef.current = [notificationCleanup];
      return;
    }

    // Extract target user from timeline route
    const pathParts = location.pathname.split('/');
    const targetUsername = pathParts[2]; // /timeline/username
    
    console.log('🚀 Efficient: Setting up timeline subscriptions for route:', location.pathname);

    // Get target user ID (for now use current user, but this should be resolved from username)
    const targetUserId = user.id; // TODO: resolve from username
    
    // Timeline subscription
    const timelineCleanup = efficientManager.subscribeToTimeline(user.id, targetUserId, {
      onTimelineEntry: (payload) => {
        console.log('📊 Efficient: Timeline entry update:', payload.eventType);
        updateTimelineCache(payload, targetUserId);
      },
      onStatusChange: (status) => {
        setIsConnected(status === 'SUBSCRIBED');
      }
    });

    // Notification subscription
    const notificationCleanup = efficientManager.subscribeToNotifications(user.id, {
      onNotification: (payload) => {
        queryClient.invalidateQueries({ queryKey: ['notifications', user.id] });
      },
      onFriendRequest: (payload) => {
        queryClient.invalidateQueries({ queryKey: ['friends', user.id] });
      },
      onStatusChange: () => {} // Already handled by timeline subscription
    });

    cleanupFunctionsRef.current = [timelineCleanup, notificationCleanup];

    return () => {
      console.log('🧹 Efficient: Route cleanup');
      cleanupFunctionsRef.current.forEach(cleanup => cleanup());
      cleanupFunctionsRef.current = [];
    };
  }, [user?.id, location.pathname, updateTimelineCache, queryClient]);

  // Global cleanup on unmount
  useEffect(() => {
    return () => {
      efficientManager.cleanup();
    };
  }, []);

  return (
    <EfficientRealtimeContext.Provider value={{ 
      subscriptionCount: efficientManager.getActiveSubscriptions(),
      isConnected 
    }}>
      {children}
    </EfficientRealtimeContext.Provider>
  );
};